/**
 * ТЗ 0107 — правила визуального редактора шаблонов договоров (без React).
 *
 * Сервер отдаёт тело договора html-ом, где каждый кусок разметки Django
 * (`{{ … }}`, `{% … %}`, подписи) — чип
 * `<span data-tpl="N" contenteditable="false" class="tpl-chip …">подпись</span>`,
 * а между строками таблицы — комментарий `<!--tpl:N-->` (span браузер вынес
 * бы из таблицы). При сохранении сервер возвращает каждому номеру его код.
 *
 * 🔴 Комментарии `<!--tpl:N-->` — часть текста: их нельзя терять. Поэтому
 * html редактора НЕ прогоняется через DOMPurify (тот режет комментарии) — он
 * пришёл с нашего сервера и санитайзится им же при сохранении. Чистится
 * только вставка из буфера обмена: в документ идёт лишь обычный текст.
 */

export interface IContractTemplateToken {
  n: number;
  /** var | tag | sign */
  kind: string;
  /** var | logic | comment | struct | sign */
  role: string;
  code: string;
  label: string;
  labelPl: string;
  removable: boolean;
  /** chip | comment */
  placement: string;
}

export interface IContractTemplateVariable {
  code: string;
  group: string;
  label: string;
  labelPl: string;
  groupLabel: string;
  groupLabelPl: string;
}

export interface IContractTemplateProblem {
  code: string;
  message: string;
  token?: number | null;
}

export type TemplateEditorLanguage = 'ru' | 'pl';

/** Атрибуты, которые редактор вешает на чипы для вида; на сервер не уходят. */
const ROLE_ATTR = 'data-role';
export const CHIP_ERROR_CLASS = 'has-error';

const COMMENT_RE = /^tpl:(\d+)$/;

const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LI',
  'UL',
  'OL',
  'TABLE',
  'TBODY',
  'THEAD',
  'TR',
  'TD',
  'TH',
  'BLOCKQUOTE',
]);

export const templateEditorLanguage = (
  language: string | undefined | null,
): TemplateEditorLanguage => (language === 'pl' ? 'pl' : 'ru');

export const tokenLabel = (
  token: Pick<IContractTemplateToken, 'label' | 'labelPl'>,
  language: TemplateEditorLanguage,
): string => (language === 'pl' ? token.labelPl || token.label : token.label);

/** Номер чипа элемента (`data-tpl`) или `null`. */
export const chipNumber = (node: Node | null): number | null => {
  if (!node || node.nodeType !== 1) return null;
  const raw = (node as Element).getAttribute('data-tpl');
  if (raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) ? n : null;
};

/** Номер токена комментария `<!--tpl:N-->` или `null`. */
export const commentNumber = (node: Node | null): number | null => {
  if (!node || node.nodeType !== 8) return null;
  const match = COMMENT_RE.exec((node.nodeValue || '').trim());
  return match ? Number(match[1]) : null;
};

/** Номер токена у узла — чипа или комментария. */
export const tokenNumberOf = (node: Node | null): number | null =>
  chipNumber(node) ?? commentNumber(node);

const collectComments = (root: Node): Comment[] => {
  const out: Comment[] = [];
  const walk = (node: Node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 8) out.push(child as Comment);
      else if (child.nodeType === 1) walk(child);
    });
  };
  walk(root);
  return out;
};

/** Узлы токенов в документе: чипы и комментарии. */
export const tokenNodes = (root: Node): Node[] => {
  const chips =
    root.nodeType === 1
      ? Array.from((root as Element).querySelectorAll('[data-tpl]'))
      : [];
  return [...chips, ...collectComments(root)];
};

/** Номера токенов, которые есть в документе. */
export const presentTokenNumbers = (root: Node): Set<number> => {
  const out = new Set<number>();
  tokenNodes(root).forEach((node) => {
    const n = tokenNumberOf(node);
    if (n !== null) out.add(n);
  });
  return out;
};

/** Номера неудаляемых токенов (логика, подписи). */
export const lockedTokenNumbers = (
  tokens: IContractTemplateToken[],
): Set<number> =>
  new Set(tokens.filter((token) => !token.removable).map((token) => token.n));

/** Неудаляемые токены, которых в документе больше нет. */
export const missingLockedTokens = (
  root: Node,
  tokens: IContractTemplateToken[],
): IContractTemplateToken[] => {
  const present = presentTokenNumbers(root);
  return tokens.filter((token) => !token.removable && !present.has(token.n));
};

/** Класс вспышки чипа («Показать» у ошибки) — снимается по `animationend`. */
export const CHIP_FLASH_CLASS = 'is-flash';
/** Классы вида, которые никогда не уходят на сервер. */
const SERVICE_CLASSES = [CHIP_ERROR_CLASS, CHIP_FLASH_CLASS];
/** Атрибуты вида и доступности на чипах; на сервер не уходят. */
const SERVICE_ATTRS = [
  ROLE_ATTR,
  'data-locked',
  'role',
  'aria-label',
  'aria-roledescription',
];

/** Что читать вслух о чипе: подпись и описание роли. */
export interface IChipDescription {
  label: string;
  roleDescription: string;
}

/**
 * Описание чипа для экранного диктора: роль токена, можно ли его удалить и
 * его подпись (`label` — текст чипа). Тексты даёт окно (i18n).
 */
export type ChipDescriber = (chip: {
  role: string;
  removable: boolean;
  label: string;
}) => IChipDescription;

const describeChip = (
  chip: Element,
  role: string,
  removable: boolean,
  describe?: ChipDescriber,
) => {
  if (!describe) return;
  const text = describe({
    role,
    removable,
    label: (chip.textContent || '').trim(),
  });
  chip.setAttribute('role', 'img');
  chip.setAttribute('aria-label', text.label);
  chip.setAttribute('aria-roledescription', text.roleDescription);
};

/**
 * Разметка для вида: роль токена (`data-role`) — цвет и значок чипа по ней;
 * чип — всегда `contenteditable=false`. С `describe` — ещё `aria-label` и
 * `aria-roledescription` («Подпись: …, нельзя удалить»).
 */
export const decorateChips = (
  root: Element,
  tokens: IContractTemplateToken[],
  describe?: ChipDescriber,
): void => {
  const byN = new Map(tokens.map((token) => [token.n, token]));
  root.querySelectorAll('[data-tpl]').forEach((chip) => {
    const n = chipNumber(chip);
    const token = n === null ? undefined : byN.get(n);
    chip.setAttribute('contenteditable', 'false');
    if (token) {
      chip.setAttribute(ROLE_ATTR, token.role);
      if (!token.removable) chip.setAttribute('data-locked', 'true');
      describeChip(chip, token.role, token.removable, describe);
    }
  });
  root.querySelectorAll('[data-tpl-new]').forEach((chip) => {
    chip.setAttribute('contenteditable', 'false');
    chip.setAttribute(ROLE_ATTR, 'var');
    describeChip(chip, 'var', true, describe);
  });
};

/**
 * Html, который уходит на сервер: без пометок вида (`data-role`,
 * `data-locked`, подсветка ошибок и вспышка, атрибуты для диктора).
 * Комментарии `<!--tpl:N-->` остаются — `innerHTML` их сохраняет.
 */
export const serializeEditorHtml = (root: Element): string => {
  const clone = root.cloneNode(true) as Element;
  clone.querySelectorAll('[data-tpl], [data-tpl-new]').forEach((chip) => {
    SERVICE_ATTRS.forEach((attr) => chip.removeAttribute(attr));
  });
  SERVICE_CLASSES.forEach((name) => {
    clone.querySelectorAll(`.${name}`).forEach((node) => {
      node.classList.remove(name);
      if (!node.getAttribute('class')) node.removeAttribute('class');
    });
  });
  return clone.innerHTML;
};

/** Новый чип переменной из палитры: сервер превратит его в `{{ code }}`. */
export const createVariableChip = (
  doc: Document,
  variable: IContractTemplateVariable,
  language: TemplateEditorLanguage,
  describe?: ChipDescriber,
): HTMLSpanElement => {
  const span = doc.createElement('span');
  span.setAttribute('data-tpl-new', `var:${variable.code}`);
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('class', 'tpl-chip tpl-var');
  span.setAttribute(ROLE_ATTR, 'var');
  span.setAttribute('title', variable.code);
  span.textContent = tokenLabel(variable, language);
  describeChip(span, 'var', true, describe);
  return span;
};

/* ---------- Целостность закрытых токенов (как проверяет сервер) ---------- */

/** Номера токенов в порядке документа (чипы и комментарии вперемешку). */
export const orderedTokenNumbers = (root: Node): number[] => {
  const out: number[] = [];
  const walk = (node: Node) => {
    node.childNodes.forEach((child) => {
      const n = tokenNumberOf(child);
      if (n !== null) {
        out.push(n);
        return;
      }
      if (child.nodeType === 1) walk(child);
    });
  };
  walk(root);
  return out;
};

export type LockedTokensProblem = 'missing' | 'order' | 'duplicate';

/**
 * Что не так с токенами документа, или `null`:
 * - `missing` — пропал неудаляемый токен;
 * - `duplicate` — номер токена встречается дважды (перетаскивание копией);
 * - `order` — неудаляемые токены переставлены относительно `expectedOrder`
 *   (порядок при открытии; без него — по номерам).
 */
export const lockedTokensProblem = (
  root: Node,
  tokens: IContractTemplateToken[],
  expectedOrder?: number[],
): LockedTokensProblem | null => {
  const locked = lockedTokenNumbers(tokens);
  const order = orderedTokenNumbers(root);
  const present = new Set(order);
  if (tokens.some((token) => !token.removable && !present.has(token.n))) {
    return 'missing';
  }
  if (present.size !== order.length) return 'duplicate';
  const lockedNow = order.filter((n) => locked.has(n));
  const expected = (
    expectedOrder ?? Array.from(locked).sort((a, b) => a - b)
  ).filter((n) => locked.has(n));
  if (
    lockedNow.length !== expected.length ||
    lockedNow.some((n, index) => n !== expected[index])
  ) {
    return 'order';
  }
  return null;
};

/* ---------- Каретка и собственная история правок ---------- */

export interface ICaretPoint {
  /** Индексы `childNodes` от корня документа до узла. */
  path: number[];
  offset: number;
}

export interface ICaret {
  start: ICaretPoint;
  end: ICaretPoint;
}

const pointOf = (
  root: Node,
  node: Node,
  offset: number,
): ICaretPoint | null => {
  const path: number[] = [];
  let current: Node | null = node;
  while (current && current !== root) {
    const parent: Node | null = current.parentNode;
    if (!parent) return null;
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, current));
    current = parent;
  }
  return current === root ? { path, offset } : null;
};

/** Позиция выделения путём по узлам — переживает замену `innerHTML`. */
export const caretOf = (root: Node, range: Range | null): ICaret | null => {
  if (!range) return null;
  const start = pointOf(root, range.startContainer, range.startOffset);
  const end = pointOf(root, range.endContainer, range.endOffset);
  return start && end ? { start, end } : null;
};

const nodeAt = (root: Node, point: ICaretPoint): [Node, number] => {
  let node: Node = root;
  for (let i = 0; i < point.path.length; i += 1) {
    const child = node.childNodes[point.path[i]];
    if (!child) {
      return [node, node.childNodes.length];
    }
    node = child;
  }
  const max =
    node.nodeType === 3 || node.nodeType === 8
      ? (node.nodeValue || '').length
      : node.childNodes.length;
  return [node, Math.min(point.offset, max)];
};

/** Диапазон по сохранённой позиции (узлы, которых уже нет, — к ближайшим). */
export const rangeFromCaret = (
  root: Node,
  caret: ICaret,
  doc: Document,
): Range | null => {
  try {
    const range = doc.createRange();
    const [startNode, startOffset] = nodeAt(root, caret.start);
    const [endNode, endOffset] = nodeAt(root, caret.end);
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
    return range;
  } catch (error) {
    return null;
  }
};

export interface IEditorSnapshot {
  /** `serializeEditorHtml` — без пометок вида. */
  html: string;
  caret: ICaret | null;
}

export const EDITOR_HISTORY_LIMIT = 100;

/**
 * История правок редактора (`createEditorHistory`): снимки документа и
 * каретки. Своя, а не браузерная: браузерный undo не знает про откат
 * порчи (замена `innerHTML` его сбрасывает), и Cmd+Z с кнопкой «Отменить»
 * вели себя по-разному.
 *
 * `record(snapshot, merge)` — новый снимок после правки; `merge` — дописать
 * к текущему (серия набора текста — один шаг отмены). Снимок, равный
 * текущему, только обновляет каретку.
 */
export interface IEditorHistory {
  reset: (snapshot: IEditorSnapshot) => void;
  current: () => IEditorSnapshot | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  size: () => number;
  record: (snapshot: IEditorSnapshot, merge?: boolean) => void;
  /** Каретка текущего снимка — куда вернуться при отмене следующей правки. */
  updateCaret: (caret: ICaret | null) => void;
  undo: () => IEditorSnapshot | null;
  redo: () => IEditorSnapshot | null;
}

export const createEditorHistory = (
  limit = EDITOR_HISTORY_LIMIT,
): IEditorHistory => {
  let entries: IEditorSnapshot[] = [];
  let index = -1;
  const current = () => entries[index] ?? null;
  const canUndo = () => index > 0;
  const canRedo = () => index >= 0 && index < entries.length - 1;
  return {
    reset: (snapshot) => {
      entries = [snapshot];
      index = 0;
    },
    current,
    canUndo,
    canRedo,
    size: () => entries.length,
    record: (snapshot, merge = false) => {
      const head = current();
      if (head && head.html === snapshot.html) {
        head.caret = snapshot.caret;
        return;
      }
      entries = entries.slice(0, index + 1);
      if (merge && index > 0) {
        entries[index] = snapshot;
        return;
      }
      entries.push(snapshot);
      if (entries.length > limit) {
        entries.splice(0, entries.length - limit);
      }
      index = entries.length - 1;
    },
    updateCaret: (caret) => {
      const head = current();
      if (head && caret) head.caret = caret;
    },
    undo: () => {
      if (!canUndo()) return null;
      index -= 1;
      return entries[index];
    },
    redo: () => {
      if (!canRedo()) return null;
      index += 1;
      return entries[index];
    },
  };
};

/**
 * Подсветка чипов из ошибок проверки. Возвращает первый подсвеченный — к
 * нему редактор прокручивает документ.
 */
export const markProblemChips = (
  root: Element,
  problems: IContractTemplateProblem[],
): Element | null => {
  root
    .querySelectorAll(`.${CHIP_ERROR_CLASS}`)
    .forEach((chip) => chip.classList.remove(CHIP_ERROR_CLASS));
  let first: Element | null = null;
  problems.forEach((problem) => {
    if (problem.token === null || problem.token === undefined) return;
    const chip = root.querySelector(`[data-tpl="${problem.token}"]`);
    if (!chip) return;
    chip.classList.add(CHIP_ERROR_CLASS);
    if (!first) first = chip;
  });
  return first;
};

const isLockedNode = (node: Node | null, locked: Set<number>): boolean => {
  const n = tokenNumberOf(node);
  return n !== null && locked.has(n);
};

const deletionHitsLockedSelection = (
  range: Range,
  root: Node,
  locked: Set<number>,
): boolean =>
  tokenNodes(root).some((node) => {
    if (!isLockedNode(node, locked)) return false;
    try {
      return range.intersectsNode(node);
    } catch (error) {
      return false;
    }
  });

/** Ближайший узел по ту сторону каретки (с подъёмом из начала/конца). */
const neighbour = (
  container: Node,
  offset: number,
  backward: boolean,
  root: Node,
): Node | null => {
  let node: Node | null;
  if (container.nodeType === 3) {
    const { length } = container.nodeValue || '';
    if (backward ? offset > 0 : offset < length) return null;
    node = container;
  } else {
    const child = backward
      ? container.childNodes[offset - 1]
      : container.childNodes[offset];
    if (child) return child;
    node = container;
  }
  // С края узла — к соседу родителя, но не за пределы документа и не через
  // границу блока: Backspace в начале абзаца склеивает абзацы, чип соседнего
  // абзаца при этом остаётся на месте.
  while (node && node !== root) {
    const sibling: Node | null = backward
      ? node.previousSibling
      : node.nextSibling;
    if (sibling) return sibling;
    const parent: Node | null = node.parentNode;
    if (parent && parent.nodeType === 1 && BLOCK_TAGS.has(parent.nodeName)) {
      return null;
    }
    node = parent;
  }
  return null;
};

/** Спуск в строчный элемент до крайнего потомка (`<b><span chip/></b>`). */
const edgeLeaf = (node: Node, backward: boolean): Node => {
  let current = node;
  while (
    current.nodeType === 1 &&
    chipNumber(current) === null &&
    !BLOCK_TAGS.has(current.nodeName) &&
    current.childNodes.length > 0
  ) {
    current = backward
      ? (current.lastChild as Node)
      : (current.firstChild as Node);
  }
  return current;
};

/**
 * Удалит ли это нажатие (Backspace/Delete, ввод поверх выделения, вырезание)
 * неудаляемый чип. Выделение — любое пересечение с таким токеном; каретка —
 * соседний узел в сторону удаления.
 */
export const deletionHitsLocked = (
  range: Range | null,
  root: Node,
  locked: Set<number>,
  direction: 'backward' | 'forward' | 'selection',
): boolean => {
  if (!range || locked.size === 0) return false;
  if (!root.contains(range.commonAncestorContainer)) return false;
  if (!range.collapsed) {
    return deletionHitsLockedSelection(range, root, locked);
  }
  if (direction === 'selection') return false;
  const backward = direction === 'backward';
  const next = neighbour(
    range.startContainer,
    range.startOffset,
    backward,
    root,
  );
  if (!next) return false;
  // Пустой текстовый узел между кареткой и чипом браузер удалит вместе с чипом.
  let candidate: Node | null = next;
  while (
    candidate &&
    candidate.nodeType === 3 &&
    !(candidate.nodeValue || '').length
  ) {
    candidate = backward ? candidate.previousSibling : candidate.nextSibling;
  }
  if (!candidate) return false;
  return isLockedNode(edgeLeaf(candidate, backward), locked);
};

/* ---------- Команды над выделением ---------- */

const closestElement = (node: Node | null, root: Node): Element | null => {
  let current: Node | null = node;
  while (current && current !== root && current.nodeType !== 1) {
    current = current.parentNode;
  }
  return current && current !== root ? (current as Element) : null;
};

const insideTable = (node: Node | null, root: Node): boolean => {
  const element = closestElement(node, root);
  const table = element?.closest('table');
  return Boolean(table && root.contains(table));
};

/**
 * Можно ли менять блоки (абзац/заголовок/список) под выделением: нельзя,
 * если выделение задевает неудаляемый токен или таблицу (браузер переложит
 * строки таблицы и комментарии `<!--tpl:N-->` уедут), или каретка стоит в
 * таблице.
 */
export const blockCommandHitsLocked = (
  range: Range | null,
  root: Node,
  locked: Set<number>,
): boolean => {
  if (!range || !root.contains(range.commonAncestorContainer)) return false;
  if (
    insideTable(range.startContainer, root) ||
    insideTable(range.endContainer, root)
  ) {
    return true;
  }
  if (range.collapsed) return false;
  if (deletionHitsLockedSelection(range, root, locked)) return true;
  if (root.nodeType !== 1) return false;
  return Array.from((root as Element).querySelectorAll('table')).some(
    (table) => {
      try {
        return range.intersectsNode(table);
      } catch (error) {
        return false;
      }
    },
  );
};

const TEXT_BLOCKS = 'p, li, h1, h2, h3, h4, h5, h6';

/**
 * Куда вставить поле, если курсора в документе нет: в конец последнего
 * текстового блока (абзац, пункт, заголовок) вне таблицы и вне чипа; нет
 * такого — в конец документа.
 */
export const fallbackInsertRange = (root: Element, doc: Document): Range => {
  const blocks = Array.from(root.querySelectorAll(TEXT_BLOCKS)).filter(
    (block) =>
      !block.closest('table') &&
      !block.closest('[data-tpl], [data-tpl-new]') &&
      root.contains(block),
  );
  const range = doc.createRange();
  const target = blocks[blocks.length - 1];
  if (target) {
    range.selectNodeContents(target);
  } else {
    range.selectNodeContents(root);
  }
  range.collapse(false);
  return range;
};

/* ---------- Палитра переменных ---------- */

export interface IVariableGroup {
  group: string;
  label: string;
  items: IContractTemplateVariable[];
}

/** Группы палитры с поиском по подписи и коду (без учёта регистра). */
export const groupTemplateVariables = (
  variables: IContractTemplateVariable[],
  query: string,
  language: TemplateEditorLanguage,
): IVariableGroup[] => {
  const needle = query.trim().toLowerCase();
  const groups = new Map<string, IVariableGroup>();
  variables.forEach((variable) => {
    const label = tokenLabel(variable, language);
    if (
      needle &&
      !label.toLowerCase().includes(needle) &&
      !variable.code.toLowerCase().includes(needle)
    ) {
      return;
    }
    const group = groups.get(variable.group) ?? {
      group: variable.group,
      label:
        language === 'pl'
          ? variable.groupLabelPl || variable.groupLabel
          : variable.groupLabel,
      items: [],
    };
    group.items.push(variable);
    groups.set(variable.group, group);
  });
  return Array.from(groups.values());
};

/* ---------- «Сравнить с действующей» ---------- */

const squash = (text: string): string => text.replace(/\s+/g, ' ').trim();

const hasBlockChild = (element: Element): boolean =>
  Array.from(element.children).some((child) => BLOCK_TAGS.has(child.nodeName));

/**
 * Текст документа по абзацам: абзац, заголовок, пункт списка, строка
 * таблицы (ячейки через « | »). Чип — его подпись.
 */
export const templateTextBlocks = (html: string, doc: Document): string[] => {
  const holder = doc.createElement('div');
  holder.innerHTML = html;
  const out: string[] = [];
  const walk = (element: Element) => {
    Array.from(element.children).forEach((child) => {
      if (child.nodeName === 'TR') {
        const cells = Array.from(child.children)
          .map((cell) => squash(cell.textContent || ''))
          .filter(Boolean);
        if (cells.length) out.push(cells.join(' | '));
        return;
      }
      if (BLOCK_TAGS.has(child.nodeName) && !hasBlockChild(child)) {
        const text = squash(child.textContent || '');
        if (text) out.push(text);
        return;
      }
      walk(child);
    });
  };
  walk(holder);
  // Текст прямо в корне (без абзаца) — одной строкой.
  const loose = squash(
    Array.from(holder.childNodes)
      .filter((node) => node.nodeType === 3)
      .map((node) => node.nodeValue || '')
      .join(' '),
  );
  if (loose) out.unshift(loose);
  return out;
};

export interface ITextDiffLine {
  type: 'same' | 'removed' | 'added';
  text: string;
}

/** Построчный diff (наибольшая общая подпоследовательность). */
export const diffTextBlocks = (
  before: string[],
  after: string[],
): ITextDiffLine[] => {
  const n = before.length;
  const m = after.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        before[i] === after[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: ITextDiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) {
      out.push({ type: 'same', text: before[i] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ type: 'removed', text: before[i] });
      i += 1;
    } else {
      out.push({ type: 'added', text: after[j] });
      j += 1;
    }
  }
  while (i < n) {
    out.push({ type: 'removed', text: before[i] });
    i += 1;
  }
  while (j < m) {
    out.push({ type: 'added', text: after[j] });
    j += 1;
  }
  return out;
};

/**
 * Только изменения и по одной строке контекста вокруг них; пропуски —
 * `null` (рисуются «…»).
 */
export const compactDiff = (
  lines: ITextDiffLine[],
  context = 1,
): Array<ITextDiffLine | null> => {
  const keep = new Set<number>();
  lines.forEach((line, index) => {
    if (line.type === 'same') return;
    for (
      let k = Math.max(0, index - context);
      k <= Math.min(lines.length - 1, index + context);
      k += 1
    ) {
      keep.add(k);
    }
  });
  const out: Array<ITextDiffLine | null> = [];
  let gap = false;
  lines.forEach((line, index) => {
    if (keep.has(index)) {
      out.push(line);
      gap = false;
    } else if (!gap) {
      out.push(null);
      gap = true;
    }
  });
  return out;
};

/* ---------- Список шаблонов ---------- */

/** Приложения, которые можно выбрать новому шаблону студента. */
export const STUDENT_TEMPLATE_APPENDIXES = [
  'none',
  'group_change',
  'dofinansowanie',
];

/**
 * Юрлица для «Создать на основе»: из прочитанных шаблонов (сервер и так
 * отдаёт только общие и своего юрлица).
 */
export const templateLegalEntities = (
  templates: Array<{
    legalEntityId?: string | null;
    legalEntityName?: string | null;
  }>,
): Array<{ id: string; name: string }> => {
  const seen = new Map<string, string>();
  templates.forEach((template) => {
    if (template.legalEntityId && !seen.has(template.legalEntityId)) {
      seen.set(
        template.legalEntityId,
        template.legalEntityName || template.legalEntityId,
      );
    }
  });
  return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
};

/**
 * Действующие шаблоны вида для выбора в форме черновика. Выбор показывается,
 * только когда их больше одного.
 */
export const activeTemplatesOfKind = <
  T extends { kind: string; isActive: boolean }
>(
  templates: T[],
  kind: string,
): T[] =>
  templates.filter((template) => template.kind === kind && template.isActive);
